import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  limit,
  runTransaction,
  onSnapshot,
  setDoc,
  writeBatch,
  arrayUnion,
  deleteField
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage, functionsService } from '../firebase';
import { notificationService } from './firestoreService';
import { formatDateToPH, getPHNow, getServiceRule, getSlotValidation } from '../utils/appointmentUtils';
import { httpsCallable } from 'firebase/functions';

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://us-central1-auravit-bb843.cloudfunctions.net";

/**
 * Safely convert a createdAt / timestamp field to milliseconds.
 * Handles: Firestore Timestamp objects, ISO strings, numbers, null/undefined.
 */
const toMs = (val) => {
  if (!val) return 0;
  if (typeof val.toMillis === 'function') return val.toMillis();   // Firestore Timestamp
  if (typeof val.toDate  === 'function') return val.toDate().getTime(); // also Timestamp
  if (typeof val === 'string') { const ms = Date.parse(val); return isNaN(ms) ? 0 : ms; }
  if (typeof val === 'number') return val;
  return 0;
};

const sendRescheduleEmailRequest = async (payload) => {
  if (!payload || !payload.uid || !payload.appointmentId) {
    throw new Error('Missing appointment or user information for reschedule email.');
  }

  const response = await fetch(`${API_BASE}/sendRescheduleEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to send reschedule email');
  }

  return data;
};

const FOLLOW_UP_ACTIVE_STATUSES = ['PENDING', 'CONFIRMED', 'CANCELLING', 'ACCEPTED'];
const FOLLOW_UP_MAX_OCCURRENCES = 500;

const validateFollowUpSchedule = async ({
  sourceAppointmentId,
  serviceId,
  serviceName,
  bookingType,
  occurrences
}) => {
  if (!auth.currentUser) {
    throw new Error('User not authenticated. Please log in again.');
  }
  if (!sourceAppointmentId) {
    throw new Error('The completed source appointment is required.');
  }
  if (!Array.isArray(occurrences) || occurrences.length < 1 || occurrences.length > FOLLOW_UP_MAX_OCCURRENCES) {
    throw new Error(`A follow-up schedule must contain between 1 and ${FOLLOW_UP_MAX_OCCURRENCES} occurrences.`);
  }
  if (!['ONLINE', 'WALK_IN'].includes(bookingType)) {
    throw new Error('Choose a valid appointment type.');
  }

  const sourceSnapshot = await getDoc(doc(db, 'appointments', sourceAppointmentId));
  if (!sourceSnapshot.exists() || sourceSnapshot.data().status !== 'COMPLETED') {
    throw new Error('Follow-up appointments can only be created from a completed appointment.');
  }
  const sourceAppointment = { id: sourceSnapshot.id, ...sourceSnapshot.data() };
  if (!sourceAppointment.uid || sourceAppointment.uid === 'walk-in') {
    throw new Error('The completed appointment is not linked to a registered patient.');
  }
  if (!sourceAppointment.patientName || !sourceAppointment.reference) {
    throw new Error('The completed appointment is missing the patient name or reference required for a follow-up.');
  }

  const serviceRule = getServiceRule(serviceId || serviceName);
  if (!serviceRule) {
    throw new Error('Choose a supported follow-up service.');
  }

  const today = formatDateToPH(new Date());
  const phNow = getPHNow();
  const nowMinutes = (phNow.hour % 24) * 60 + phNow.minute;
  const seen = new Set();
  const results = occurrences.map(({ date, time }) => {
    let reason = null;
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
    const timeMatch = /^(1[0-2]|[1-9]):([0-5]\d) (AM|PM)$/.exec(time || '');

    if (!dateMatch || !timeMatch) {
      reason = 'A valid date and time are required.';
    } else {
      const [, yearText, monthText, dayText] = dateMatch;
      const year = Number(yearText);
      const month = Number(monthText);
      const day = Number(dayText);
      const calendarDate = new Date(Date.UTC(year, month - 1, day));
      if (
        calendarDate.getUTCFullYear() !== year ||
        calendarDate.getUTCMonth() !== month - 1 ||
        calendarDate.getUTCDate() !== day
      ) {
        reason = 'The selected date is invalid.';
      } else if (date < today) {
        reason = 'The date must be in the future.';
      } else if ([0, 6].includes(calendarDate.getUTCDay())) {
        reason = 'The clinic is closed on weekends.';
      } else {
        const hour = Number(timeMatch[1]) % 12 + (timeMatch[3] === 'PM' ? 12 : 0);
        const minute = Number(timeMatch[2]);
        if (date === today && hour * 60 + minute <= nowMinutes) {
          reason = 'The selected time has already passed.';
        } else {
          const slotValidation = getSlotValidation(time, serviceId || serviceName);
          if (!slotValidation.valid) {
            reason = slotValidation.reason === 'lunch'
              ? 'The selected time overlaps the clinic lunch break.'
              : 'The selected time is outside the service hours.';
          }
        }
      }
    }

    const key = `${date}|${time}`;
    if (!reason && seen.has(key)) {
      reason = 'This schedule contains the same date and time more than once.';
    }
    seen.add(key);

    return { date, time, available: !reason, reason };
  });

  const dates = [...new Set(results.filter(result => result.available).map(result => result.date))];
  const appointmentsByDate = new Map();
  for (let index = 0; index < dates.length; index += 30) {
    const dateChunk = dates.slice(index, index + 30);
    const dateConstraint = dateChunk.length === 1
      ? where('date', '==', dateChunk[0])
      : where('date', 'in', dateChunk);
    const snapshot = await getDocs(query(collection(db, 'appointments'), dateConstraint));
    snapshot.docs.forEach(appointmentDoc => {
      const data = appointmentDoc.data();
      const dateAppointments = appointmentsByDate.get(data.date) || [];
      dateAppointments.push(data);
      appointmentsByDate.set(data.date, dateAppointments);
    });
  }

  results.forEach(result => {
    if (!result.available) return;
    const conflict = (appointmentsByDate.get(result.date) || []).some(appointment => {
      if (!FOLLOW_UP_ACTIVE_STATUSES.includes(appointment.status)) return false;

      const sameService = appointment.serviceId === serviceId || appointment.serviceName === serviceName;
      if (!sameService || appointment.time !== result.time) return false;

      if (serviceRule.separateBookingType === false) return true;
      const existingBookingType = appointment.bookingType || (appointment.type === 'walk-in' ? 'WALK_IN' : 'ONLINE');
      return existingBookingType === bookingType;
    });

    if (conflict) {
      result.available = false;
      result.reason = 'This service slot is already booked for the selected time.';
    }
  });

  return {
    sourceAppointment,
    occurrences: results,
    valid: results.every(result => result.available)
  };
};

/**
 * STRICT STATUS MAPPING (Backend)
 * - PENDING: Pending Approval
 * - CONFIRMED: Confirmed
 * - CANCELLING: Cancelling... (User requested cancellation for a confirmed appointment)
 * - COMPLETED: Completed
 * - CANCELLED_BY_USER: Cancelled + reason
 * - CANCELLED_BY_PRO: Cancelled + reason
 * (RESCHEDULE_REQUESTED_BY_PRO & RESCHEDULE_APPROVED deprecated - direct date/time updates with audit trail)
 */

// ===== Helper Functions =====

/**
 * Validate reschedule reason
 * - Min 10 chars, Max 500 chars
 * - No HTML/scripts
 * - Trim whitespace
 * - Professional wording check (handled by UI options, but sanitized here)
 */
const validateRescheduleReason = (reason) => {
  if (!reason || reason.trim().length === 0) {
    return { valid: false, error: "Please provide a reason for rescheduling." };
  }

  const sanitized = reason.trim().replace(/<[^>]*>?/gm, ''); // Basic HTML sanitization
  
  if (sanitized.length < 10) {
    return { valid: false, error: "Reason must be at least 10 characters long." };
  }
  
  if (sanitized.length > 500) {
    return { valid: false, error: "Reason must not exceed 500 characters." };
  }
  
  return { valid: true, error: null, sanitized };
};

/**
 * Check if appointment can be rescheduled to a new slot
 * Reservation Rules:
 * - STI Testing / Screening: 1 ONLINE, 1 WALK_IN
 * - Family Planning / Contraceptive Services: 1 TOTAL only
 * - Reproductive Health Consultation: 1 ONLINE, 1 WALK_IN
 * 
 * Logic:
 * 1. Find all appointments for the same date, time, and service.
 * 2. If Family Planning: Any match is a conflict.
 * 3. Others: Only match with the SAME bookingType is a conflict.
 */
const checkSlotAvailabilityForReschedule = async (appointmentId, proId, serviceId, serviceName, newDate, newTime, bookingType = "ONLINE") => {
  if (!appointmentId || !newDate || !newTime) {
    return { available: false, reason: "Invalid parameters" };
  }

  try {
    const q = query(
      collection(db, 'appointments'),
      where('date', '==', newDate),
      where('time', '==', newTime)
    );
    
    const snap = await getDocs(q);
    const rule = getServiceRule(serviceId || serviceName);
    
    const conflicts = snap.docs.filter(doc => {
      // Exclude the current appointment being rescheduled
      if (doc.id === appointmentId) return false;
      
      const data = doc.data();
      // Only active statuses count as conflicts
      if (!['PENDING', 'CONFIRMED', 'ACCEPTED'].includes(data.status)) return false;

      // Check if it's the same service
      const sameService = data.serviceId === serviceId || data.serviceName === serviceName;
      if (!sameService) return false;

      // Enforce reservation rules
      if (rule && rule.separateBookingType === false) {
        // Family Planning: 1 TOTAL slot only
        return true; 
      } else {
        // Others: 1 ONLINE + 1 WALK_IN
        const existingBookingType = data.bookingType || (data.type === "walk-in" ? "WALK_IN" : "ONLINE");
        return existingBookingType === bookingType;
      }
    });
    
    if (conflicts.length > 0) {
      return { available: false, reason: "This service slot is already booked for the selected time." };
    }
    
    return { available: true, reason: null };
  } catch (err) {
    console.error("Error checking slot availability for reschedule:", err);
    return { available: false, reason: "Error checking availability" };
  }
};

export const appointmentService = {
  // 1. Fetch available staff (accountType === "Staff" and roles includes "Medical Professional" or "Peer Educator")
  getAvailableStaff: async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('accountType', '==', 'Staff'),
        limit(50)
      );
      const snap = await getDocs(q);
      return snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => 
          user.roles?.includes("Medical Professional") || 
          user.roles?.includes("Peer Educator")
        );
    } catch (err) {
      console.error("Error getting available staff:", err);
      return [];
    }
  },

  // 2. Check slot availability
  checkSlotAvailability: async (proId, date, time) => {
    if (!proId || !date || !time) return false;
    
    // Explicitly wait for Auth if it's still initializing
    if (!auth.currentUser) {
      await new Promise(resolve => {
        const unsubscribe = auth.onAuthStateChanged(user => {
          unsubscribe();
          resolve(user);
        });
      });
    }

    if (!auth.currentUser) {
      console.error("checkSlotAvailability: User not authenticated.");
      return false;
    }

    try {
      const q = query(
        collection(db, 'appointments'),
        where('proId', '==', proId),
        where('date', '==', date),
        where('time', '==', time)
      );
      const snap = await getDocs(q);
      
      // Filter in-memory to avoid complex index requirements or permission issues with "in" queries
      const activeAppts = snap.docs.filter(doc => {
        const data = doc.data();
        return ['PENDING', 'CONFIRMED', 'ACCEPTED'].includes(data.status);
      });
      
      return activeAppts.length === 0;
    } catch (err) {
      if (err.code === 'permission-denied') {
        console.warn("Permission denied checking availability. Proceeding anyway.");
        return true; 
      }
      console.error("Error checking slot availability:", err);
      return false;
    }
  },

  // 2.1 Fetch booked slots for a specific professional and date
  getBookedSlots: async (date) => {
    if (!date) return [];
    
    if (!auth.currentUser) {
      await new Promise(resolve => {
        const unsubscribe = auth.onAuthStateChanged(user => {
          unsubscribe();
          resolve(user);
        });
      });
    }

    if (!auth.currentUser) {
      console.error("getBookedSlots: User not authenticated.");
      return [];
    }

    try {
      const q = query(
        collection(db, 'appointments'),
        where('date', '==', date)
      );
      const snap = await getDocs(q);
      return snap.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            time: data.time,
            serviceId: data.serviceId,
            serviceName: data.serviceName,
            status: data.status,
            bookingType: data.bookingType || (data.type === "walk-in" ? "WALK_IN" : "ONLINE")
          };
        })
        .filter(appt => ['PENDING', 'CONFIRMED', 'ACCEPTED'].includes(appt.status));
    } catch (err) {
      console.error("Error getting booked slots:", err);
      return [];
    }
  },

  // 3. Create appointment (Slot Locking Logic using Transaction)
  createAppointment: async (appointmentData) => {
    const { date, time, uid, serviceId, serviceName, type, bookingType: providedBookingType } = appointmentData;
    const bookingType = providedBookingType || (type === "walk-in" ? "WALK_IN" : "ONLINE");

    // For online bookings, proId is optional at creation as it may be assigned later by staff
    if (!uid || !date || !time) {
      throw new Error("Missing required appointment fields (uid, date, time)");
    }

    if (!auth.currentUser) {
      throw new Error("User not authenticated. Please log in again.");
    }

    const conflictQuery = query(
      collection(db, 'appointments'),
      where('date', '==', date),
      where('time', '==', time)
    );

    const hasConflict = (snap) => {
      const rule = getServiceRule(serviceId || serviceName);
      return snap.docs.some(doc => {
        const data = doc.data();
        const activeStatus = ['PENDING', 'CONFIRMED', 'CANCELLING', 'ACCEPTED'].includes(data.status);
        if (!activeStatus) return false;

        const matchesService = data.serviceId === serviceId || data.serviceName === serviceName;
        if (!matchesService) return false;

        // Enforce reservation rules
        if (rule && rule.separateBookingType === false) {
          // Family Planning: 1 TOTAL slot only
          return true; 
        } else {
          // Others: 1 ONLINE + 1 WALK_IN
          const existingBookingType = data.bookingType || (data.type === "walk-in" ? "WALK_IN" : "ONLINE");
          return existingBookingType === bookingType;
        }
      });
    };

    // 1. Check for conflicts BEFORE the transaction
    try {
      const snap = await getDocs(conflictQuery);
      if (hasConflict(snap)) {
        throw new Error("This service slot is already booked for the selected time.");
      }
    } catch (err) {
      if (err.code === 'permission-denied') {
        console.warn("Permission denied in pre-check. Proceeding with creation attempt.");
      } else {
        console.error("Error checking for conflicts:", err);
        throw err;
      }
    }

    try {
      const desiredStatus = appointmentData.status || 'PENDING';
      const result = await runTransaction(db, async (transaction) => {
        // Create new appointment
        const appointmentsRef = collection(db, 'appointments');
        const newDocRef = doc(appointmentsRef);
        
        transaction.set(newDocRef, {
          ...appointmentData,
          bookingType,
          status: desiredStatus,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        return { id: newDocRef.id, ...appointmentData, bookingType, status: desiredStatus };
      });

      // Trigger booking notification after successful transaction
      try {
        const appointmentWithId = { 
          id: result.id, 
          ...appointmentData, 
          bookingType, 
          status: result.status || desiredStatus
        };
        await notificationService.createAppointmentNotification(appointmentWithId);
      } catch (notifErr) {
        console.error("Error creating booking notification:", notifErr);
        // Don't fail the appointment if notification fails
      }

      return result;
    } catch (err) {
      console.error("Error creating appointment:", err);
      throw err;
    }
  },

  validateFollowUpSchedule: async (schedule) => {
    const result = await validateFollowUpSchedule(schedule);
    return { valid: result.valid, occurrences: result.occurrences };
  },

  createFollowUpAppointments: async ({
    sourceAppointmentId,
    serviceId,
    serviceName,
    appointmentType,
    bookingType,
    occurrences
  }) => {
    if (!['scheduled', 'walk-in'].includes(appointmentType)) {
      throw new Error('Choose a valid appointment type.');
    }
    const expectedBookingType = appointmentType === 'walk-in' ? 'WALK_IN' : 'ONLINE';
    if (bookingType !== expectedBookingType) {
      throw new Error('The appointment type and booking type do not match.');
    }

    const validation = await validateFollowUpSchedule({
      sourceAppointmentId,
      serviceId,
      serviceName,
      bookingType,
      occurrences
    });
    const failedOccurrence = validation.occurrences.find(occurrence => !occurrence.available);
    if (failedOccurrence) {
      throw new Error(`Follow-up on ${failedOccurrence.date} at ${failedOccurrence.time}: ${failedOccurrence.reason}`);
    }

    const seriesId = doc(collection(db, 'followUpSeries')).id;
    const batch = writeBatch(db);
    const createdAppointments = occurrences.map((occurrence, index) => {
      const appointmentRef = doc(collection(db, 'appointments'));
      const referenceDate = occurrence.date.replace(/-/g, '').slice(2);
      const appointment = {
        clinicId: validation.sourceAppointment.clinicId || 'park-ave',
        clinicName: validation.sourceAppointment.clinicName || 'Park Avenue Clinic Branch',
        date: occurrence.date,
        patientName: validation.sourceAppointment.patientName,
        patientDob: validation.sourceAppointment.patientDob || null,
        proId: null,
        proName: null,
        reference: `FU-${referenceDate}-${appointmentRef.id.toUpperCase()}`,
        serviceId,
        serviceName,
        status: 'CONFIRMED',
        time: occurrence.time,
        type: appointmentType === 'walk-in' ? 'walk-in' : 'scheduled',
        bookingType,
        appointmentType,
        uid: validation.sourceAppointment.uid,
        isFollowUp: true,
        originalAppointmentId: sourceAppointmentId,
        originalAppointmentReference: validation.sourceAppointment.reference || null,
        followUpSeriesId: seriesId,
        followUpOccurrence: index + 1
      };

      batch.set(appointmentRef, {
        ...appointment,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: appointmentRef.id, ...appointment };
    });

    await batch.commit();

    await Promise.all(createdAppointments.map(async appointment => {
      try {
        await notificationService.createAppointmentNotification(appointment);
      } catch (error) {
        console.error('Error creating follow-up appointment notification:', error);
      }
    }));

    return createdAppointments;
  },

  // 3. Get user's appointments (Search/Filter implemented in UI using this data)
  getUserAppointments: async (uid) => {
    if (!uid) return [];
    
    if (!auth.currentUser) {
      await new Promise(resolve => {
        const unsubscribe = auth.onAuthStateChanged(user => {
          unsubscribe();
          resolve(user);
        });
      });
    }

    if (!auth.currentUser) {
      console.error("getUserAppointments: User not authenticated.");
      return [];
    }

    try {
      const q = query(
        collection(db, 'appointments'),
        where('uid', '==', uid),
        limit(100)
      );
      const snap = await getDocs(q);
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // In-memory sort by createdAt descending (newest first)
      return appointments.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
    } catch (err) {
      console.error("Error getting user appointments:", err);
      return [];
    }
  },

  // 3b. Get user's appointments with server-side status filtering
  getUserAppointmentsByStatus: async (uid, status) => {
    if (!uid) return [];
    
    if (!auth.currentUser) {
      await new Promise(resolve => {
        const unsubscribe = auth.onAuthStateChanged(user => {
          unsubscribe();
          resolve(user);
        });
      });
    }

    if (!auth.currentUser) {
      console.error("getUserAppointmentsByStatus: User not authenticated.");
      return [];
    }

    try {
      const q = query(
        collection(db, 'appointments'),
        where('uid', '==', uid),
        where('status', '==', status),
        limit(100)
      );
      const snap = await getDocs(q);
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // In-memory sort by createdAt descending (newest first)
      return appointments.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
    } catch (err) {
      console.error("Error getting user appointments by status:", err);
      return [];
    }
  },

  // 4. Get appointments for Staff Queue
  getProAppointments: async (proId) => {
    if (!proId) return [];
    try {
      const q = query(
        collection(db, 'appointments'),
        where('proId', '==', proId),
        limit(100)
      );
      const snap = await getDocs(q);
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      return appointments.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
    } catch (err) {
      console.error("Error getting pro appointments:", err);
      return [];
    }
  },

  getFollowUpAppointmentsForSources: async (sourceAppointmentIds) => {
    const sourceIds = [...new Set((sourceAppointmentIds || []).filter(Boolean))];
    const snapshots = [];

    for (let index = 0; index < sourceIds.length; index += 30) {
      const sourceIdChunk = sourceIds.slice(index, index + 30);
      const followUpsSnapshot = await getDocs(query(
        collection(db, 'appointments'),
        where('originalAppointmentId', 'in', sourceIdChunk)
      ));
      snapshots.push(...followUpsSnapshot.docs);
    }

    return snapshots
      .map(appointmentDoc => ({ id: appointmentDoc.id, ...appointmentDoc.data() }))
      .filter(appointment =>
        appointment.isFollowUp === true &&
        sourceIds.includes(appointment.originalAppointmentId)
      );
  },

  getConfirmedAppointments: async () => {
    try {
      const q = query(
        collection(db, 'appointments'),
        where('status', '==', 'CONFIRMED'),
        limit(100)
      );
      const snap = await getDocs(q);
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      return appointments.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
    } catch (err) {
      console.error("Error getting confirmed appointments:", err);
      return [];
    }
  },

  normalizeAssignServices: (services) => {
    if (!services) return [];
    const values = Array.isArray(services) ? services : [services];
    return values
      .filter((service) => typeof service === 'string' && service.trim().length > 0)
      .map((service) => service.trim());
  },

  doesAppointmentMatchAssignedServices: (appointment, assignServices) => {
    const services = appointmentService.normalizeAssignServices(assignServices);
    if (!services.length) return false;

    const serviceName = (appointment.serviceName || '').toString().trim().toLowerCase();
    const serviceId = (appointment.serviceId || '').toString().trim().toLowerCase();

    return services.some((service) => {
      const normalized = service.toLowerCase().trim();
      if (!normalized) return false;

      if (normalized === serviceName || normalized === serviceId) return true;
      if (serviceName && serviceName.includes(normalized)) return true;
      if (serviceId && serviceId.includes(normalized)) return true;
      if (normalized && serviceName && normalized.includes(serviceName)) return true;
      if (normalized && serviceId && normalized.includes(serviceId)) return true;

      const isHivMatch = normalized.includes('hiv') && (serviceName.includes('hiv') || serviceName.includes('sti') || serviceId.includes('hiv') || serviceId.includes('sti'));
      const isStiMatch = normalized.includes('sti') && (serviceName.includes('hiv') || serviceName.includes('sti') || serviceId.includes('hiv') || serviceId.includes('sti'));
      if (isHivMatch || isStiMatch) return true;

      return false;
    });
  },

  filterAppointmentsForAssignedServices: (appointments, assignServices) => {
    const services = appointmentService.normalizeAssignServices(assignServices);
    if (!services.length) return [];
    return appointments.filter((appt) => appointmentService.doesAppointmentMatchAssignedServices(appt, services));
  },

  getPendingAppointmentsForStaff: async (staffUserData) => {
    if (!staffUserData || staffUserData.accountType !== 'Staff') return [];

    const isMedicalProfessional =
      staffUserData.role === 'Medical Professional' ||
      (Array.isArray(staffUserData.roles) && staffUserData.roles.includes('Medical Professional'));

    const hasAssignedServices =
      staffUserData.servicesAssigned === true ||
      staffUserData.servicesassigned === true;

    const assignServices = appointmentService.normalizeAssignServices(staffUserData.assignServices);

    try {
      const q = query(
        collection(db, 'appointments'),
        where('status', '==', 'PENDING'),
        limit(100)
      );
      const snap = await getDocs(q);
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (!hasAssignedServices || !assignServices.length) {
        return appointments.sort((a, b) => {
          const timeA = toMs(a.createdAt);
          const timeB = toMs(b.createdAt);
          return timeB - timeA;
        });
      }

      const filtered = appointmentService.filterAppointmentsForAssignedServices(appointments, assignServices);
      return filtered.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
    } catch (err) {
      console.error('Error getting pending appointments for staff:', err);
      return [];
    }
  },

  subscribeToPendingAppointmentsForStaff: (staffUserData, callback) => {
    if (!staffUserData || staffUserData.accountType !== 'Staff') return () => {};

    const isMedicalProfessional =
      staffUserData.role === 'Medical Professional' ||
      (Array.isArray(staffUserData.roles) && staffUserData.roles.includes('Medical Professional'));

    const hasAssignedServices =
      staffUserData.servicesAssigned === true ||
      staffUserData.servicesassigned === true;

    const assignServices = appointmentService.normalizeAssignServices(staffUserData.assignServices);

    if (!isMedicalProfessional && (!hasAssignedServices || !assignServices.length)) {
      return () => {};
    }

    const q = query(
      collection(db, 'appointments'),
      where('status', '==', 'PENDING'),
      limit(100)
    );

    return onSnapshot(q, (snap) => {
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const filtered = (isMedicalProfessional && !hasAssignedServices) ? appointments : appointmentService.filterAppointmentsForAssignedServices(appointments, assignServices);
      const sorted = filtered.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
      callback(sorted);
    }, (err) => {
      console.error('Error subscribing to pending appointments for staff:', err);
    });
  },

  // Get single appointment by ID
  getAppointmentById: async (appointmentId) => {
    if (!appointmentId) return null;
    try {
      const apptDoc = await getDoc(doc(db, 'appointments', appointmentId));
      if (apptDoc.exists()) {
        return { id: apptDoc.id, ...apptDoc.data() };
      }
      return null;
    } catch (err) {
      console.error("Error getting appointment:", err);
      return null;
    }
  },

  // 4.1 Subscribe to appointments for Staff Queue (Real-time)
  subscribeToProAppointments: (proId, callback) => {
    if (!proId) return () => {};
    const q = query(
      collection(db, 'appointments'),
      where('proId', '==', proId),
      limit(100)
    );

    return onSnapshot(q, (snap) => {
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = appointments.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
      callback(sorted);
    }, (err) => {
      console.error("Error subscribing to pro appointments:", err);
    });
  },

  // Subscribe to all CONFIRMED appointments (Real-time)
  subscribeToConfirmedAppointments: (callback) => {
    const q = query(
      collection(db, 'appointments'),
      where('status', '==', 'CONFIRMED'),
      limit(100)
    );

    return onSnapshot(q, (snap) => {
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = appointments.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
      callback(sorted);
    }, (err) => {
      console.error("Error subscribing to confirmed appointments:", err);
    });
  },

  // 3.1 Subscribe to user's appointments (Real-time)
  subscribeToUserAppointments: (uid, callback) => {
    if (!uid) return () => {};
    const q = query(
      collection(db, 'appointments'),
      where('uid', '==', uid),
      limit(100)
    );

    return onSnapshot(q, (snap) => {
      const appointments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = appointments.sort((a, b) => {
        const timeA = toMs(a.createdAt);
        const timeB = toMs(b.createdAt);
        return timeB - timeA;
      });
      callback(sorted);
    }, (err) => {
      console.error("Error subscribing to user appointments:", err);
    });
  },

  // 5. Update Status with reason (for cancellations)
  updateStatus: async (appointmentId, status, additionalData = {}) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      
      const updateData = {
        status,
        ...additionalData,
        updatedAt: serverTimestamp()
      };

      // Set completedAt when status changes to COMPLETED
      if (status === 'COMPLETED') {
        updateData.completedAt = serverTimestamp();
        
        // Checkpoint 3: Add blockchain fields for health record integrity
        // GRACE_PERIOD_MINUTES = 5 for testing, will be 10080 (7 days) for production
        const GRACE_PERIOD_MINUTES = 5;
        const completedAtTime = new Date();
        const editDeadline = new Date(completedAtTime.getTime() + (GRACE_PERIOD_MINUTES * 60 * 1000));
        
        updateData.editDeadline = editDeadline.toISOString();
        updateData.blockchain = {
          status: 'pending',
          network: 'polygon-amoy',
          createdAt: completedAtTime.toISOString()
        };
      }

      await updateDoc(apptRef, updateData);

      // Trigger persistent notification
      try {
        const apptDoc = await getDoc(apptRef);
        if (apptDoc.exists()) {
          const apptData = { id: apptDoc.id, ...apptDoc.data() };
          await notificationService.createAppointmentNotification(apptData);
        }
      } catch (notifErr) {
        console.error("Error creating status update notification:", notifErr);
      }

      return true;
    } catch (err) {
      console.error("Error updating status:", err);
      throw err;
    }
  },

  // 6. NEW Reschedule Appointment (Direct update with audit trail)
  rescheduleAppointment: async (appointmentId, newDate, newTime, rescheduleReason, staffProfile) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Staff authentication required");

      // Validate reason
      const reasonValidation = validateRescheduleReason(rescheduleReason);
      if (!reasonValidation.valid) {
        throw new Error(reasonValidation.error);
      }

      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      
      if (!apptSnap.exists()) {
        throw new Error("Appointment not found");
      }

      const apptData = apptSnap.data();
      const oldDate = apptData.date;
      const oldTime = apptData.time;
      const serviceId = apptData.serviceId;
      const serviceName = apptData.serviceName;
      const bookingType = apptData.bookingType || (apptData.type === "walk-in" ? "WALK_IN" : "ONLINE");

      // Validate slot availability
      const slotCheck = await checkSlotAvailabilityForReschedule(
        appointmentId, 
        apptData.proId, 
        serviceId,
        serviceName,
        newDate, 
        newTime,
        bookingType
      );
      
      if (!slotCheck.available) {
        throw new Error(slotCheck.reason);
      }

      const staffName = staffProfile?.fullName || 
        `${staffProfile?.firstName || ''} ${staffProfile?.lastName || ''}`.trim() || 
        currentUser?.displayName || 'Medical Professional';

      const staffIdentifier = staffProfile?.staffId || currentUser.uid;
      const isAcceptedAppointment = apptData.status === 'ACCEPTED';
      const nextStatus = isAcceptedAppointment ? 'ACCEPTED' : 'CONFIRMED';

      // Preserve the current assignment behavior: confirmed appointments stay unassigned,
      // while accepted appointments keep their assigned professional.
      await updateDoc(apptRef, {
        date: newDate,
        time: newTime,
        status: nextStatus,
        proId: isAcceptedAppointment ? (apptData.proId ?? null) : null,
        proName: isAcceptedAppointment ? (apptData.proName ?? null) : null,
        auditRescheduledBy: staffIdentifier,
        auditPreviousDate: oldDate,
        auditPreviousTime: oldTime,
        auditRescheduledAt: serverTimestamp(),
        // Legacy audit for backward compatibility
        rescheduleAudit: {
          rescheduledByStaffId: staffIdentifier,
          rescheduledByName: staffName,
          oldDate: oldDate,
          oldTime: oldTime,
          newDate: newDate,
          newTime: newTime,
          reason: reasonValidation.sanitized,
          rescheduledAt: serverTimestamp()
        },
        // Clear all old reschedule-related fields
        proposedDate: null,
        proposedTime: null,
        rescheduleBy: null,
        rescheduleReason: null,
        updatedAt: serverTimestamp()
      });

      // Trigger in-app notification for the user
      try {
        const updatedAppt = await getDoc(apptRef);
        if (updatedAppt.exists()) {
          const updatedData = { id: updatedAppt.id, ...updatedAppt.data() };
          await notificationService.createAppointmentNotification(updatedData);
        }
      } catch (notifErr) {
        console.error("Error creating reschedule notification:", notifErr);
      }

      return { success: true, appointmentId };
    } catch (err) {
      console.error("Error rescheduling appointment:", err);
      throw err;
    }
  },

  // 7. Data Access Flow
  requestDataAccess: async (appointmentId, proId, durationWeeks, proStaffId, proProfile = {}) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      const accessScope = proProfile?.accessScope || 'medical_professional';
      const weeks = Math.min(4, Math.max(1, parseInt(durationWeeks, 10) || 1));
      const staffName = proProfile?.fullName ||
        proProfile?.name ||
        `${proProfile?.firstName || ''} ${proProfile?.lastName || ''}`.trim() ||
        auth.currentUser?.displayName ||
        auth.currentUser?.email ||
        'Medical Professional';
      const staffRole = proProfile?.role ||
        (Array.isArray(proProfile?.roles) ? proProfile.roles.find(role => role === 'Medical Professional') : null) ||
        'Medical Professional';
      const staffDepartment = proProfile?.department || proProfile?.assignedDepartment || 'Medical Services';
      const staffEmail = proProfile?.email || auth.currentUser?.email || '';

      // Pre-check: Verify no pending request exists before attempting transaction
      // This fails fast for rapid double-clicks or stale UI state
      const apptSnapshot = await getDoc(apptRef);
      if (!apptSnapshot.exists()) {
        throw new Error("Appointment not found");
      }
      const existingRequests = apptSnapshot.data().dataAccessRequests || {};
      // Check if this professional already has a pending request
      const existingRequestForPro = Object.values(existingRequests).find(
        req => (req.professionalId === proId || req.proId === proId) &&
          (req.accessScope || 'medical_professional') === accessScope &&
          req.status === 'PENDING'
      );
      if (existingRequestForPro) {
        throw new Error("You already have a pending data access request for this appointment.");
      }

      const requestId = `${appointmentId}_${proId}_${Date.now()}`;
      let apptData = null;
      let requestData = null;

      await runTransaction(db, async (transaction) => {
        const apptSnap = await transaction.get(apptRef);
        if (!apptSnap.exists()) throw new Error("Appointment not found");
        apptData = apptSnap.data();

        const existingRequests = apptData.dataAccessRequests || {};
        // Check again in transaction
        const existingRequestForPro = Object.values(existingRequests).find(
          req => (req.professionalId === proId || req.proId === proId) &&
            (req.accessScope || 'medical_professional') === accessScope &&
            req.status === 'PENDING'
        );
        if (existingRequestForPro) {
          throw new Error("You already have a pending data access request for this appointment.");
        }

        requestData = {
          requestId,
          appointmentId,
          appointmentReference: apptData.reference,
          reference: apptData.reference,
          professionalId: proId,
          proId,
          accessScope,
          proStaffId: proStaffId || proProfile?.staffId || proId,
          proName: staffName,
          professionalName: staffName,
          proEmail: staffEmail,
          professionalEmail: staffEmail,
          proRole: staffRole,
          proDepartment: staffDepartment,
          durationWeeks: weeks,
          requestedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          status: 'PENDING',
          requestedData: 'Appointment records',
          dataRequested: 'Appointment records',
          purpose: 'Patient care coordination',
          appointmentType: apptData.serviceName || 'Reproductive Health Services',
          serviceType: apptData.serviceName || 'Reproductive Health Services'
        };

        // Add to dataAccessRequests map instead of overwriting
        transaction.update(apptRef, {
          [`dataAccessRequests.${requestId}`]: requestData,
          updatedAt: serverTimestamp()
        });

        const privacyRef = doc(db, 'users', apptData.uid, 'data_privacy', requestId);
        transaction.set(privacyRef, requestData);
      });

      // 3. Trigger persistent notification
      try {
        await notificationService.createDataAccessRequestNotification(
          apptData.uid, 
          proId, 
          apptData.serviceName,
          { requestId, professionalName: staffName, professionalEmail: staffEmail }
        );
      } catch (notifErr) {
        console.error("Error creating data access notification:", notifErr);
      }

      return { success: true, requestId };
    } catch (err) {
      console.error("Error requesting access:", err);
      throw err;
    }
  },

  approveDataAccess: async (appointmentId, requestId, durationWeeks) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");
      const apptData = apptSnap.data();
      
      // Find the specific request in dataAccessRequests map
      const dataAccessRequests = apptData.dataAccessRequests || {};
      const request = dataAccessRequests[requestId];

      if (!request) throw new Error("Request not found");

      // Calculate expiresAt: approvedAt + (durationWeeks * 7 days)
      // Use serverTimestamp for approvedAt, then calculate expiresAt
      const approvedAt = new Date();
      const expiresAt = new Date(approvedAt.getTime() + (durationWeeks * 7 * 24 * 60 * 60 * 1000));

      const batch = writeBatch(db);

      // 1. Update the specific request in dataAccessRequests map
      batch.update(apptRef, {
        [`dataAccessRequests.${requestId}.status`]: 'ACTIVE',
        [`dataAccessRequests.${requestId}.approvedAt`]: serverTimestamp(),
        [`dataAccessRequests.${requestId}.expiresAt`]: expiresAt.toISOString(),
        dataAccessRequest: deleteField(),
        updatedAt: serverTimestamp()
      });

      // 2. Remove from data_privacy
      const privacyRef = doc(db, 'users', apptData.uid, 'data_privacy', requestId);
      batch.delete(privacyRef);

      // 3. Add to access_history
      const historyRef = doc(db, 'users', apptData.uid, 'access_history', requestId);
      // Filter out undefined values to prevent Firestore errors
      const cleanedRequest = Object.fromEntries(
        Object.entries(request).filter(([_, value]) => value !== undefined)
      );
      batch.set(historyRef, {
        ...cleanedRequest,
        status: 'ACTIVE',
        approvedAt: serverTimestamp(),
        expiresAt: expiresAt.toISOString(),
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      // Trigger notification for professional that their request was approved
      if (apptData && request) {
        try {
          // Fetch patient data to get UIC
          const patientDoc = await getDoc(doc(db, 'users', apptData.uid));
          const patientData = patientDoc.exists() ? patientDoc.data() : {};
          const patientUIC = patientData.uniqueIdentifierCode || patientData.accountSetup?.uniqueIdentifierCode || 'N/A';
          const patientName = patientData.fullName || 'Patient';

          await notificationService.createDataAccessRequestNotification(
            request.proId,
            apptData.uid,
            apptData.serviceName,
            {
              requestId,
              patientUIC,
              patientName,
              patientUid: apptData.uid,
              appointmentId: appointmentId,
              isProfessionalNotification: true,
              action: 'approved'
            }
          );
        } catch (notifErr) {
          console.error("Error creating professional notification for approved access:", notifErr);
        }
      }

      return true;
    } catch (err) {
      console.error("Error approving access:", err);
      throw err;
    }
  },

  denyDataAccess: async (appointmentId, requestId) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");
      const apptData = apptSnap.data();
      
      // Find the specific request in dataAccessRequests map
      const dataAccessRequests = apptData.dataAccessRequests || {};
      const request = dataAccessRequests[requestId];

      if (!request) throw new Error("Request not found");

      const batch = writeBatch(db);

      // 1. Update the specific request in dataAccessRequests map
      batch.update(apptRef, {
        [`dataAccessRequests.${requestId}.status`]: 'DENIED',
        [`dataAccessRequests.${requestId}.deniedAt`]: serverTimestamp(),
        dataAccessRequest: deleteField(),
        updatedAt: serverTimestamp()
      });

      // 2. Remove from data_privacy
      const privacyRef = doc(db, 'users', apptData.uid, 'data_privacy', requestId);
      batch.delete(privacyRef);

      // 3. Add to access_history
      const historyRef = doc(db, 'users', apptData.uid, 'access_history', requestId);
      // Filter out undefined values to prevent Firestore errors
      const cleanedRequest = Object.fromEntries(
        Object.entries(request).filter(([_, value]) => value !== undefined)
      );
      batch.set(historyRef, {
        ...cleanedRequest,
        status: 'DENIED',
        deniedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      // Trigger notification for professional that their request was denied
      if (apptData && request) {
        try {
          // Fetch patient data to get UIC
          const patientDoc = await getDoc(doc(db, 'users', apptData.uid));
          const patientData = patientDoc.exists() ? patientDoc.data() : {};
          const patientUIC = patientData.uniqueIdentifierCode || patientData.accountSetup?.uniqueIdentifierCode || 'N/A';
          const patientName = patientData.fullName || 'Patient';

          await notificationService.createDataAccessRequestNotification(
            request.proId,
            apptData.uid,
            apptData.serviceName,
            {
              requestId,
              patientUIC,
              patientName,
              patientUid: apptData.uid,
              appointmentId: appointmentId,
              isProfessionalNotification: true,
              action: 'denied'
            }
          );
        } catch (notifErr) {
          console.error("Error creating professional notification for denied access:", notifErr);
        }
      }

      return true;
    } catch (err) {
      console.error("Error denying access:", err);
      throw err;
    }
  },

  terminateDataAccess: async (appointmentId, requestId, revokedByUser = false) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      
      // Get data before updating to trigger notification
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");
      const apptData = apptSnap.data();
      
      // Find the specific request in dataAccessRequests map
      const dataAccessRequests = apptData.dataAccessRequests || {};
      const request = dataAccessRequests[requestId];

      if (!request) throw new Error("Request not found");

      const terminationStatus = revokedByUser ? 'REVOKED' : 'EXPIRED';

      const batch = writeBatch(db);

      // 1. Update the specific request in dataAccessRequests map
      batch.update(apptRef, {
        [`dataAccessRequests.${requestId}.status`]: terminationStatus,
        [`dataAccessRequests.${requestId}.terminatedAt`]: serverTimestamp(),
        dataAccessRequest: deleteField(),
        updatedAt: serverTimestamp()
      });

      // 2. Add to access_history (it should already be there, but we update status)
      const historyRef = doc(db, 'users', apptData.uid, 'access_history', requestId);
      // Filter out undefined values to prevent Firestore errors
      const cleanedRequest = Object.fromEntries(
        Object.entries(request).filter(([_, value]) => value !== undefined)
      );
      batch.set(historyRef, {
        ...cleanedRequest,
        status: terminationStatus,
        terminatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

      await batch.commit();

      // Trigger persistent notification for termination
      if (apptData && request) {
        try {
          // Fetch patient data to get UIC
          const patientDoc = await getDoc(doc(db, 'users', apptData.uid));
          const patientData = patientDoc.exists() ? patientDoc.data() : {};
          const patientUIC = patientData.uniqueIdentifierCode || patientData.accountSetup?.uniqueIdentifierCode || 'N/A';
          const patientName = patientData.fullName || 'Patient';

          await notificationService.createDataAccessTerminationNotification(
            apptData.uid,
            request.proId,
            apptData.serviceName,
            false, // isExpired = false (it was manually terminated)
            patientUIC,
            patientName
          );
        } catch (notifErr) {
          console.error("Error creating data access termination notification:", notifErr);
        }
      }

      return true;
    } catch (err) {
      console.error("Error terminating access:", err);
      throw err;
    }
  },

  // Method to handle automatic expiration
  handleDataAccessExpiration: async (appointmentId) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");
      const apptData = apptSnap.data();
      
      // Get all data access requests
      const dataAccessRequests = apptData.dataAccessRequests || {};
      const requestIds = Object.keys(dataAccessRequests);
      
      if (requestIds.length === 0) return false;

      let expiredAny = false;
      const batch = writeBatch(db);

      // Fetch patient data once for notifications
      const patientDoc = await getDoc(doc(db, 'users', apptData.uid));
      const patientData = patientDoc.exists() ? patientDoc.data() : {};
      const patientUIC = patientData.uniqueIdentifierCode || patientData.accountSetup?.uniqueIdentifierCode || 'N/A';
      const patientName = patientData.fullName || 'Patient';

      for (const requestId of requestIds) {
        const request = dataAccessRequests[requestId];
        
        // Skip if already expired or notified
        if (request.expirationNotified) continue;

        // Only proceed if it's currently ACTIVE and actually expired
        if (request?.status === 'ACTIVE' && request.expiresAt) {
          const expiryDate = new Date(request.expiresAt);
          if (expiryDate <= new Date()) {
            console.log(`appointmentService: Detecting expiration for request ${requestId} on appointment ${appointmentId}`);
            
            // Update the specific request in dataAccessRequests map
            batch.update(apptRef, {
              [`dataAccessRequests.${requestId}.status`]: 'EXPIRED',
              [`dataAccessRequests.${requestId}.expirationNotified`]: true,
              updatedAt: serverTimestamp()
            });

            // Update access_history
            const historyRef = doc(db, 'users', apptData.uid, 'access_history', requestId);
            batch.set(historyRef, {
              ...request,
              status: 'EXPIRED',
              expirationNotified: true,
              updatedAt: serverTimestamp()
            }, { merge: true });

            expiredAny = true;

            // Trigger notifications for expiration
            try {
              // Notify patient
              await notificationService.createDataAccessTerminationNotification(
                apptData.uid,
                request.proId,
                apptData.serviceName,
                true, // isExpired = true
                patientUIC,
                patientName
              );

              // Notify professional
              await notificationService.createDataAccessRequestNotification(
                request.proId,
                apptData.uid,
                apptData.serviceName,
                {
                  requestId,
                  patientUIC,
                  patientName,
                  patientUid: apptData.uid,
                  appointmentId: appointmentId,
                  isProfessionalNotification: true,
                  action: 'terminated'
                }
              );
            } catch (notifErr) {
              console.error("Error creating data access expiration notification:", notifErr);
            }
          }
        }
      }

      if (expiredAny) {
        await batch.commit();
      }

      return expiredAny;
    } catch (err) {
      console.error("Error handling data access expiration:", err);
      return false;
    }
  },

  // 8. Clinical Notes
  addClinicalNote: async (appointmentId, noteText, sharedWithPatient) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      // Fetch professional info
      const proRef = doc(db, 'users', user.uid);
      const proSnap = await getDoc(proRef);
      const proData = proSnap.data();
      const proName = proData?.firstName && proData?.lastName 
        ? `${proData.firstName} ${proData.lastName}`
        : proData?.displayName || "Medical Professional";

      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");
      
      const apptData = apptSnap.data();
      
      // Check if appointment is COMPLETED and outside edit deadline (Checkpoint 3)
      if (apptData.status === 'COMPLETED') {
        // Check blockchain status first - if read_only, block immediately
        if (apptData.blockchain?.status === 'read_only') {
          throw new Error("This appointment record is read-only and cannot be modified.");
        }
        
        // Use new editDeadline field if available, otherwise fall back to legacy 7-day check
        if (apptData.editDeadline) {
          const deadline = new Date(apptData.editDeadline);
          const now = new Date();
          
          if (now > deadline) {
            // Option 3: Trigger on-demand finalization when deadline expired
            try {
              const finalizeFunction = httpsCallable(functionsService, 'finalizeBlockchainRecord');
              await finalizeFunction({ appointmentId });
              console.log(`✅ Triggered on-demand finalization for appointment ${appointmentId}`);
            } catch (finalizeError) {
              console.error("Failed to trigger on-demand finalization:", finalizeError);
              // Continue with the error even if finalization fails
            }
            
            throw new Error("This appointment record is read-only and cannot be modified.");
          }
        } else {
          // Legacy fallback for appointments without editDeadline
          const referenceDate = apptData.completedAt?.toDate ? apptData.completedAt.toDate() : 
                               apptData.updatedAt?.toDate ? apptData.updatedAt.toDate() :
                               new Date(apptData.date);
          
          const diffTime = new Date() - referenceDate;
          const diffDays = diffTime / (1000 * 60 * 60 * 24);
          
          if (diffDays > 7) {
            throw new Error("This appointment is older than 7 days and cannot be modified.");
          }
        }
      }
      
      const currentNotes = apptData?.notes || [];
      
      const newNote = {
        noteId: Date.now().toString(),
        content: noteText.trim(),
        visibility: sharedWithPatient ? "shared" : "confidential",
        createdAt: new Date().toISOString(),
        createdBy: proName,
        proId: user.uid,
        proName: proName
      };

      await updateDoc(apptRef, {
        notes: [...currentNotes, newNote],
        // Maintain backward compatibility for existing UI components
        clinicalNotes: [...(apptData?.clinicalNotes || []), {
          id: Date.now(),
          text: noteText.trim(),
          shared: sharedWithPatient,
          createdAt: new Date().toISOString()
        }],
        updatedAt: serverTimestamp()
      });

      // Trigger notification if appointment is already COMPLETED
      try {
        if (apptData.status === 'COMPLETED') {
          // Fetch patient data to get UIC
          const patientDoc = await getDoc(doc(db, 'users', apptData.uid));
          const patientData = patientDoc.exists() ? patientDoc.data() : {};
          const patientUIC = patientData.uniqueIdentifierCode || patientData.accountSetup?.uniqueIdentifierCode || 'N/A';
          const patientName = patientData.fullName || 'Patient';

          await notificationService.createRecordUpdateNotification(
            apptData.uid,
            apptData.reference,
            apptData.serviceName,
            appointmentId,
            patientUIC,
            patientName
          );
        }
      } catch (notifErr) {
        console.error("Error triggering record update notification for clinical note:", notifErr);
      }

      return true;
    } catch (err) {
      console.error("Error adding clinical note:", err);
      throw err;
    }
  },

  /**
   * Upload an attachment and save metadata to Firestore
   * @param {string} appointmentId 
   * @param {File} file 
   * @param {string} note 
   * @param {string} uploadedBy (Staff Name)
   */
  uploadAttachment: async (appointmentId, files, note, uploadedBy) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      
      // Fetch appointment to check grace period
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) {
        throw new Error("Appointment not found");
      }
      
      const apptData = apptSnap.data();
      
      // Check if appointment is COMPLETED and outside edit deadline (Checkpoint 3)
      if (apptData.status === 'COMPLETED') {
        // Check blockchain status first - if read_only, block immediately
        if (apptData.blockchain?.status === 'read_only') {
          throw new Error("This appointment record is read-only and cannot be modified.");
        }
        
        // Use new editDeadline field if available, otherwise fall back to legacy 7-day check
        if (apptData.editDeadline) {
          const deadline = new Date(apptData.editDeadline);
          const now = new Date();
          
          if (now > deadline) {
            // Option 3: Trigger on-demand finalization when deadline expired
            try {
              const finalizeFunction = httpsCallable(functionsService, 'finalizeBlockchainRecord');
              await finalizeFunction({ appointmentId });
              console.log(`✅ Triggered on-demand finalization for appointment ${appointmentId}`);
            } catch (finalizeError) {
              console.error("Failed to trigger on-demand finalization:", finalizeError);
              // Continue with the error even if finalization fails
            }
            
            throw new Error("This appointment record is read-only and cannot be modified.");
          }
        } else {
          // Legacy fallback for appointments without editDeadline
          const referenceDate = apptData.completedAt?.toDate ? apptData.completedAt.toDate() : 
                               apptData.updatedAt?.toDate ? apptData.updatedAt.toDate() :
                               new Date(apptData.date);
          
          const diffTime = new Date() - referenceDate;
          const diffDays = diffTime / (1000 * 60 * 60 * 24);
          
          if (diffDays > 7) {
            throw new Error("This appointment is older than 7 days and cannot be modified.");
          }
        }
      }
      
      const attachmentDataList = [];

      // Ensure files is an array
      const fileList = Array.isArray(files) ? files : [files];

      // 1. Upload all files to Firebase Storage
      for (const file of fileList) {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        const storagePath = `appointments/${appointmentId}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        const snapshot = await uploadBytes(storageRef, file);
        const fileUrl = await getDownloadURL(snapshot.ref);

        attachmentDataList.push({
          id: `att_${timestamp}_${Math.random().toString(36).substr(2, 5)}`,
          fileName: file.name,
          fileType: file.type,
          fileUrl: fileUrl,
          uploadedBy: uploadedBy,
          uploadedAt: new Date().toISOString(),
          note: note ? note.trim() : ""
        });
      }

      // 2. Prepare Firestore update
      const updateData = {
        updatedAt: serverTimestamp()
      };

      if (attachmentDataList.length > 0) {
        updateData.attachments = arrayUnion(...attachmentDataList);
      }

      // Feature 4: Store notes/findings in a dedicated array for persistence
      if (note && note.trim()) {
        const noteData = {
          id: `note_${Date.now()}`,
          text: note.trim(),
          createdAt: new Date().toISOString(),
          createdBy: uploadedBy
        };
        updateData.attachmentNotes = arrayUnion(noteData);
        // Also update legacy field if it exists for backward compatibility
        updateData.clinicalNote = note.trim();
      }

      await updateDoc(apptRef, updateData);

      // Trigger notification if appointment is already COMPLETED
      try {
        const apptDoc = await getDoc(apptRef);
        if (apptDoc.exists()) {
          const apptData = apptDoc.data();
          if (apptData.status === 'COMPLETED') {
            // Fetch patient data to get UIC
            const patientDoc = await getDoc(doc(db, 'users', apptData.uid));
            const patientData = patientDoc.exists() ? patientDoc.data() : {};
            const patientUIC = patientData.uniqueIdentifierCode || patientData.accountSetup?.uniqueIdentifierCode || 'N/A';
            const patientName = patientData.fullName || 'Patient';

            await notificationService.createRecordUpdateNotification(
              apptData.uid,
              apptData.reference,
              apptData.serviceName,
              appointmentId,
              patientUIC,
              patientName
            );
          }
        }
      } catch (notifErr) {
        console.error("Error triggering record update notification:", notifErr);
      }

      return true;
    } catch (err) {
      console.error("Error uploading attachment:", err);
      throw err;
    }
  },

  updateAttachmentContent: async (appointmentId, files, note, uploadedBy, existingAttachments = [], removedAttachmentIds = [], noteId = null) => {
    const apptRef = doc(db, 'appointments', appointmentId);
    const apptSnap = await getDoc(apptRef);
    if (!apptSnap.exists()) throw new Error("Appointment not found");

    const apptData = apptSnap.data();
    if (apptData.status === 'COMPLETED') {
      if (apptData.blockchain?.status === 'read_only' || apptData.blockchain?.status === 'verified') {
        throw new Error("This appointment record is read-only and cannot be modified.");
      }

      const deadline = apptData.editDeadline
        ? new Date(apptData.editDeadline)
        : new Date(
            (apptData.completedAt?.toDate ? apptData.completedAt.toDate() : new Date(apptData.completedAt || apptData.updatedAt || apptData.date))
              .getTime() + (7 * 24 * 60 * 60 * 1000)
          );
      if (Number.isNaN(deadline.getTime()) || deadline <= new Date()) {
        throw new Error("This appointment record is read-only and cannot be modified.");
      }
    }

    const removedIds = new Set(removedAttachmentIds);
    const retainedAttachments = existingAttachments.filter((attachment) => !removedIds.has(attachment.id));
    const uploadedAttachments = [];
    const fileList = Array.isArray(files) ? files : (files ? [files] : []);

    for (const file of fileList) {
      const timestamp = Date.now();
      const storageRef = ref(storage, `appointments/${appointmentId}/${timestamp}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      uploadedAttachments.push({
        id: `att_${timestamp}_${Math.random().toString(36).substr(2, 5)}`,
        fileName: file.name,
        fileType: file.type,
        fileUrl: await getDownloadURL(snapshot.ref),
        uploadedBy,
        uploadedAt: new Date().toISOString(),
        note: note ? note.trim() : ""
      });
    }

    const nextNotes = [...(apptData.attachmentNotes || [])];
    if (noteId) {
      const noteIndex = nextNotes.findIndex((item) => item.id === noteId);
      if (noteIndex >= 0) {
        nextNotes[noteIndex] = {
          ...nextNotes[noteIndex],
          text: note.trim(),
          updatedAt: new Date().toISOString(),
          updatedBy: uploadedBy
        };
      }
    } else if (note.trim()) {
      nextNotes.push({
        id: `note_${Date.now()}`,
        text: note.trim(),
        createdAt: new Date().toISOString(),
        createdBy: uploadedBy
      });
    }

    await updateDoc(apptRef, {
      attachments: [...retainedAttachments, ...uploadedAttachments],
      attachmentNotes: nextNotes,
      clinicalNote: note.trim(),
      updatedAt: serverTimestamp()
    });

    await Promise.all( existingAttachments
      .filter((attachment) => removedIds.has(attachment.id) && attachment.fileUrl)
      .map(async (attachment) => {
        try {
          await deleteObject(ref(storage, attachment.fileUrl));
        } catch (error) {
          console.warn("Unable to remove replaced attachment from storage:", attachment.fileName, error);
        }
      })
    );
  },

  // 9. Personal Notes (User only) - V2 Array implementation
  addPersonalNote: async (appointmentId, noteText) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");

      const apptData = apptSnap.data();
      const currentPersonalNotes = apptData?.personalNotes || [];

      // Fetch user info for createdBy
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const userName = userData?.firstName && userData?.lastName 
        ? `${userData.firstName} ${userData.lastName}`
        : userData?.displayName || "Patient";

      const newNote = {
        id: Date.now().toString(),
        text: noteText.trim(),
        createdAt: new Date().toISOString(),
        createdBy: userName,
        uid: user.uid
      };

      await updateDoc(apptRef, {
        personalNotes: [...currentPersonalNotes, newNote],
        // Maintain backward compatibility if needed, though V2 is preferred
        personalNote: noteText.trim(), 
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (err) {
      console.error("Error adding personal note:", err);
      throw err;
    }
  },

  // Update an existing personal note
  updatePersonalNote: async (appointmentId, noteId, updatedText) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");

      const apptData = apptSnap.data();
      const currentPersonalNotes = apptData?.personalNotes || [];

      const updatedNotes = currentPersonalNotes.map(note => 
        note.id === noteId ? { ...note, text: updatedText.trim(), updatedAt: new Date().toISOString() } : note
      );

      await updateDoc(apptRef, {
        personalNotes: updatedNotes,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (err) {
      console.error("Error updating personal note:", err);
      throw err;
    }
  },

  // Delete a personal note
  deletePersonalNote: async (appointmentId, noteId) => {
    try {
      const apptRef = doc(db, 'appointments', appointmentId);
      const apptSnap = await getDoc(apptRef);
      if (!apptSnap.exists()) throw new Error("Appointment not found");

      const apptData = apptSnap.data();
      const currentPersonalNotes = apptData?.personalNotes || [];

      const updatedNotes = currentPersonalNotes.filter(note => note.id !== noteId);

      await updateDoc(apptRef, {
        personalNotes: updatedNotes,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (err) {
      console.error("Error deleting personal note:", err);
      throw err;
    }
  },

  // 11. Fetch user details by UID
  getUserDetails: async (uid) => {
    if (!uid) return null;
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        console.log("User details found in Firestore:", data);
        return { id: userSnap.id, ...data };
      }
      console.warn(`No user document found in Firestore for UID: ${uid}`);
      return null;
    } catch (err) {
      if (err.code === 'permission-denied') {
        console.error("Permission denied fetching user details. Check your Firestore Security Rules for the 'users' collection.", err);
      } else {
        console.error("Error getting user details:", err);
      }
      return null;
    }
  }
};
